import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Crown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 20,
    features: ['Browse jobs', 'Submit up to 5 bids/month', 'Basic profile', 'Email support']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 30,
    popular: true,
    features: ['Unlimited bids', 'Featured profile', 'Priority support', 'Profile analytics']
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 50,
    features: ['All Pro features', 'Featured in search', 'Priority job alerts', 'Dedicated support']
  }
];

export const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchProfile();
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/contractors/${user.id}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated! Welcome to your new plan.');
        fetchProfile();
        setCheckingPayment(false);
        // Clear the session_id from URL
        navigate('/subscription', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status. Please try again.');
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await axios.post(`${API}/payments/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
          {checkingPayment && (
            <p className="text-stone-400 mt-4">Verifying payment...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="subscription-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/contractor/dashboard" 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </Link>

        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl text-white uppercase">Subscription Plans</h1>
          <p className="text-stone-400 mt-2">Choose the plan that fits your business</p>
        </div>

        {/* Current Subscription Status */}
        {profile?.subscription_active && (
          <div className="industrial-card p-6 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Crown className="w-10 h-10 text-orange-500" />
              <div>
                <h3 className="font-heading text-xl text-white uppercase">
                  Current Plan: {profile.subscription_tier}
                </h3>
                <p className="text-stone-400">
                  Active until {new Date(profile.subscription_expires).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {PLANS.map((plan) => {
            const isCurrentPlan = profile?.subscription_tier === plan.id && profile?.subscription_active;
            
            return (
              <div 
                key={plan.id}
                className={`industrial-card p-8 relative ${
                  plan.popular ? 'border-orange-500' : ''
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-1 text-sm font-heading uppercase rounded-sm">
                    Most Popular
                  </div>
                )}
                
                <h3 className="font-heading text-2xl text-white uppercase">{plan.name}</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="font-heading text-5xl text-orange-500">${plan.price}</span>
                  <span className="text-stone-400 ml-2">/month</span>
                </div>
                
                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-stone-300">
                      <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan || processingPlan === plan.id}
                  data-testid={`subscribe-${plan.id}`}
                  className={`w-full h-12 mt-8 font-heading uppercase tracking-wide rounded-sm ${
                    isCurrentPlan
                      ? 'bg-stone-700 text-stone-400 cursor-not-allowed'
                      : plan.popular 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : 'bg-stone-800 hover:bg-stone-700 text-white border border-stone-700'
                  }`}
                >
                  {processingPlan === plan.id ? (
                    <Loader2 className="animate-spin" />
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Free Tier Info */}
        <div className="mt-12 text-center text-stone-400">
          <p>Without a subscription, you can browse jobs but cannot submit bids.</p>
        </div>
      </main>
    </div>
  );
};

export const SubscriptionSuccessPage = () => {
  return <SubscriptionPage />;
};
